import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { galleryPages } from '../data/content';

/**
 * Real Volume 4 pages. Swipeable on touch (native scroll-snap), arrows and
 * dots on desktop. Shows 1 page on mobile, 2 on tablet, 3 on desktop.
 */
export function InsidePagesCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = galleryPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (slide) track.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
      if (atEnd) {
        setActive(total - 1);
        return;
      }
      const slides = Array.from(track.children) as HTMLElement[];
      let nearest = 0;
      slides.forEach((slide, index) => {
        if (Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[nearest].offsetLeft - track.scrollLeft)) {
          nearest = index;
        }
      });
      setActive(nearest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, [total]);
  return <div aria-roledescription="carousel" aria-label="Pages réelles de Makeup Instructor Ready, Volume 4" data-id="element-15" data-mp="3oshc90sejiqb">
      <div className="mb-5 hidden justify-end gap-2 md:flex" data-id="element-16" data-mp="3trkfqgsqhgw6">
        <ArrowButton label="Page précédente" onClick={() => goTo(active - 1)} data-id="element-17" data-mp="1mgnez1s24ma">
          <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-18" data-mp="1900j2yc22j3m" />
        </ArrowButton>
        <ArrowButton label="Page suivante" onClick={() => goTo(active + 1)} data-id="element-19" data-mp="1czsvjm4kl9e9">
          <ChevronRightIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-20" data-mp="3a0ypnwig53q2" />
        </ArrowButton>
      </div>

      <div ref={trackRef} className="no-scrollbar relative flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2" data-id="element-21" data-mp="351c1is12y32h">
        {galleryPages.map((page, index) => <figure key={page.src} className="w-[84%] flex-none snap-start sm:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)]" aria-roledescription="slide" aria-label={`${index + 1} sur ${total} : ${page.title}`} data-id="element-22" data-mp="3e321im912abv" data-mpi={index}>
            <div className="overflow-hidden rounded-[18px] border border-brand-charcoal/10 bg-white shadow-[0_30px_60px_-40px_rgba(59,27,87,0.5)]" data-id="element-23" data-mp="2m9t1mu2cr54o">
              <img src={page.src} alt={`${page.title} — page réelle du Volume 4`} width={692} height={864} loading={index < 2 ? 'eager' : 'lazy'} decoding="async" draggable={false} className="block aspect-[4/5] h-auto w-full select-none object-cover" data-id="element-24" data-mp="2vcc2x9qhp9ot" />
            </div>
            <figcaption className="mt-4 text-center font-body text-[13px] font-bold uppercase tracking-[0.22em] text-brand-plum" data-id="element-25" data-mp="mxdxnbshrj53">
              {page.word}
            </figcaption>
          </figure>)}
      </div>

      <div className="mt-6 flex items-center justify-center gap-2.5" data-id="element-26" data-mp="e4iq2bfjxbc5">
        {galleryPages.map((page, index) => <button key={page.src} type="button" aria-label={`Afficher : ${page.title}`} aria-current={index === active} onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-plum focus-visible:ring-offset-2 ${index === active ? 'w-8 bg-brand-plum' : 'w-2 bg-brand-charcoal/20 hover:bg-brand-charcoal/35'}`} data-id="element-27" data-mp="11s0m4j5umvkx" data-mpi={index} />)}
      </div>
    </div>;
}
function ArrowButton({
  label,
  onClick,
  children
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-charcoal/12 bg-white text-brand-charcoal transition-[background-color,transform] duration-150 ease-out hover:bg-brand-lavender active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-plum" data-id="element-28" data-mp="3o2c66c223uvw">
      {children}
    </button>;
}